/**
 * Clickstream constants
 * https://docs.google.com/document/d/14clVJHxBi4RRcKysz3E0JYaNzKb_yy7A2op4C4KK9MA/edit#heading=h.6qzs6hyvjh7h
 **/

import type { ZrmLeaseInfo } from '@zg-rentals/clickstream';
import {
  getZrmLeaseInfo,
  getZrmListingInfo,
  getZrmUserInfo,
  type ZrmClickstreamLease,
  type ZrmClickstreamListing,
  type ZrmClickstreamUser,
} from '@zg-rentals/clickstream';
import { trackLeaseListingUserEvent, trackUserListingEvent } from '.';

function getEnvelopeLeaseDashboard(eventTypeId) {
  return {
    event_template_id: '177', // No touchy
    event_template_version_id: '1',
    event_type_id: eventTypeId,
    event_type_version_id: '1',
  };
}

function getEnvelopeZillowLease(eventTypeId) {
  return {
    event_template_id: '168', // No touchy
    event_template_version_id: '1',
    event_type_id: eventTypeId,
    event_type_version_id: '1',
  };
}

function getEnvelopeLease(eventTypeId) {
  return {
    event_template_id: '283', // No touchy
    event_template_version_id: '1',
    event_type_id: eventTypeId,
    event_type_version_id: '1',
  };
}

export type LeaseListingUserEventArgs = {
  user: ZrmClickstreamUser;
  listing: ZrmClickstreamListing;
  lease: ZrmClickstreamLease;
};

type LeaseListingUserEventWithCustomZrmLease = Omit<LeaseListingUserEventArgs, 'lease'> & {
  zrmLeaseInfo: ZrmLeaseInfo['zrm_lease_info'];
};

type ListingUserEventArgs = {
  user: ZrmClickstreamUser;
  listing: ZrmClickstreamListing;
};

// https://dataportal.zillowgroup.net/event-registry/2955
export const emitButtonToContinueStartZillowLeaseEvent = ({
  zrmLeaseInfo,
  user,
  listing,
}: LeaseListingUserEventWithCustomZrmLease) =>
  trackLeaseListingUserEvent({
    envelope: getEnvelopeLeaseDashboard('2955'),
    clickstream_trigger: {
      trigger_location_nm: 'leases_dashboard',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'button_to_continue',
    },
    semantic: {
      semantic_event_nm: 'start_zillow_lease',
      topic_tag_txt: ['zrm_leases'],
    },
    zrm_lease_info: zrmLeaseInfo,
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/2956
export const emitButtonToContinueStartSelfUploadEvent = ({
  zrmLeaseInfo,
  user,
  listing,
}: LeaseListingUserEventWithCustomZrmLease) =>
  trackLeaseListingUserEvent({
    envelope: getEnvelopeLeaseDashboard('2956'),
    clickstream_trigger: {
      trigger_location_nm: 'leases_dashboard',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'button_to_continue',
    },
    semantic: {
      semantic_event_nm: 'start_self_upload_lease',
      topic_tag_txt: ['zrm_leases'],
    },
    zrm_lease_info: zrmLeaseInfo,
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/2957
export const emitButtonToContinueStartSignedSelfUploadEvent = ({
  zrmLeaseInfo,
  user,
  listing,
}: LeaseListingUserEventWithCustomZrmLease) =>
  trackLeaseListingUserEvent({
    envelope: getEnvelopeLeaseDashboard('2957'),
    clickstream_trigger: {
      trigger_location_nm: 'leases_dashboard',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'button_to_continue',
    },
    semantic: {
      semantic_event_nm: 'start_signed_self_upload_lease',
      topic_tag_txt: ['zrm_leases'],
    },
    zrm_lease_info: zrmLeaseInfo,
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/2958
export const emitCreateLeaseStartLeaseEvent = ({
  zrmLeaseInfo,
  user,
  listing,
}: LeaseListingUserEventWithCustomZrmLease) =>
  trackLeaseListingUserEvent({
    envelope: getEnvelopeLeaseDashboard('2958'),
    clickstream_trigger: {
      trigger_location_nm: 'leases_dashboard',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'create_lease',
    },
    semantic: {
      semantic_event_nm: 'start_lease',
      topic_tag_txt: ['zrm_leases'],
    },
    zrm_lease_info: zrmLeaseInfo,
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/2959
export const emitRequestSignatureWizardEvent = ({ lease, user, listing }: LeaseListingUserEventArgs) =>
  trackLeaseListingUserEvent({
    envelope: getEnvelopeLeaseDashboard('2959'),
    clickstream_trigger: {
      trigger_location_nm: 'zillow_lease_wizard',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'send_request_for_signature',
    },
    semantic: {
      semantic_event_nm: 'request_signature',
      topic_tag_txt: ['zrm_leases'],
    },
    ...getZrmLeaseInfo(lease),
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/2960
export const emitRequestElectronicSignaturesEvent = ({ lease, user, listing }: LeaseListingUserEventArgs) =>
  trackLeaseListingUserEvent({
    envelope: getEnvelopeLeaseDashboard('2960'),
    clickstream_trigger: {
      trigger_location_nm: 'upload_lease_flow',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'request_electronic_signatures',
    },
    semantic: {
      semantic_event_nm: 'request_signature',
      topic_tag_txt: ['zrm_leases'],
    },
    ...getZrmLeaseInfo(lease),
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/3491
export const emitPickupWhereYouLeftOffEvent = ({ user, listing }: ListingUserEventArgs) =>
  trackUserListingEvent({
    envelope: getEnvelopeLeaseDashboard('3491'),
    clickstream_trigger: {
      trigger_location_nm: 'leases_dashboard',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'pickup_where_you_left_off',
    },
    semantic: {
      semantic_event_nm: 'continue_lease',
      topic_tag_txt: ['zrm_leases'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/3492
export const emitFinishCreatingLeaseEvent = ({ user, listing }: ListingUserEventArgs) =>
  trackUserListingEvent({
    envelope: getEnvelopeLeaseDashboard('3492'),
    clickstream_trigger: {
      trigger_location_nm: 'leases_dashboard',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'finish_creating_lease',
    },
    semantic: {
      semantic_event_nm: 'finish_creating_lease',
      topic_tag_txt: ['zrm_leases'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/3493
export const emitButtonForOverflowActiveEvent = ({ user, listing }: ListingUserEventArgs) =>
  trackUserListingEvent({
    envelope: getEnvelopeLeaseDashboard('3493'),
    clickstream_trigger: {
      trigger_location_nm: 'leases_dashboard',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'active_lease',
      trigger_source_nm: 'button_for_overflow',
    },
    semantic: {
      semantic_event_nm: 'view_overflow',
      topic_tag_txt: ['zrm_leases'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/3494
export const emitButtonForOverflowIncompleteDraftEvent = ({ user, listing }: ListingUserEventArgs) =>
  trackUserListingEvent({
    envelope: getEnvelopeLeaseDashboard('3494'),
    clickstream_trigger: {
      trigger_location_nm: 'leases_dashboard',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'incomplete_draft',
      trigger_source_nm: 'button_for_overflow',
    },
    semantic: {
      semantic_event_nm: 'view_overflow',
      topic_tag_txt: ['zrm_leases'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/3495
export const emitButtonForOverflowCompleteDraftEvent = ({ user, listing }: ListingUserEventArgs) =>
  trackUserListingEvent({
    envelope: getEnvelopeLeaseDashboard('3495'),
    clickstream_trigger: {
      trigger_location_nm: 'leases_dashboard',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'incomplete_draft',
      trigger_source_nm: 'button_for_overflow',
    },
    semantic: {
      semantic_event_nm: 'view_overflow',
      topic_tag_txt: ['zrm_leases'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/3496
export const emitDuplicateLeaseEvent = ({ user, listing }: ListingUserEventArgs) =>
  trackUserListingEvent({
    envelope: getEnvelopeLeaseDashboard('3496'),
    clickstream_trigger: {
      trigger_location_nm: 'leases_dashboard',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'lease_overflow',
      trigger_source_nm: 'duplicate_lease',
    },
    semantic: {
      semantic_event_nm: 'duplicate_lease',
      topic_tag_txt: ['zrm_leases'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/3497
export const emitDeleteDraftEvent = ({ user, listing }: ListingUserEventArgs) =>
  trackUserListingEvent({
    envelope: getEnvelopeLeaseDashboard('3497'),
    clickstream_trigger: {
      trigger_location_nm: 'leases_dashboard',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'lease_overflow',
      trigger_source_nm: 'delete_draft',
    },
    semantic: {
      semantic_event_nm: 'delete_draft',
      topic_tag_txt: ['zrm_leases'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/3498
export const emitDeleteDraftYesButtonEvent = ({ user, listing }: ListingUserEventArgs) =>
  trackUserListingEvent({
    envelope: getEnvelopeLeaseDashboard('3498'),
    clickstream_trigger: {
      trigger_location_nm: 'leases_dashboard',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'lease_overflow',
      trigger_source_nm: 'pop_up_confirm_delete',
    },
    semantic: {
      semantic_event_nm: 'confirm_delete',
      topic_tag_txt: ['zrm_leases'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/3499
export const emitSetupRentPaymentEvent = ({ user, listing }: ListingUserEventArgs) =>
  trackUserListingEvent({
    envelope: getEnvelopeLeaseDashboard('3499'),
    clickstream_trigger: {
      trigger_location_nm: 'leases_dashboard',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'lease_overflow',
      trigger_source_nm: 'setup_rent_payment',
    },
    semantic: {
      semantic_event_nm: 'setup_rent_payment',
      topic_tag_txt: ['zrm_leases'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/3500
export const emitResendSigningEmailsEvent = ({ user, listing }: ListingUserEventArgs) =>
  trackUserListingEvent({
    envelope: getEnvelopeLeaseDashboard('3500'),
    clickstream_trigger: {
      trigger_location_nm: 'leases_dashboard',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'lease_overflow',
      trigger_source_nm: 'resend_email_for_signing',
    },
    semantic: {
      semantic_event_nm: 'resend_email',
      topic_tag_txt: ['zrm_leases'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/3501
export const emitViewLeaseEvent = ({ user, listing }: ListingUserEventArgs) =>
  trackUserListingEvent({
    envelope: getEnvelopeLeaseDashboard('3501'),
    clickstream_trigger: {
      trigger_location_nm: 'leases_dashboard',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'view_lease',
    },
    semantic: {
      semantic_event_nm: 'view_lease',
      topic_tag_txt: ['zrm_leases'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/3502
export const emitDeactivateLeaseEvent = ({ lease, user, listing }: LeaseListingUserEventArgs) =>
  trackLeaseListingUserEvent({
    envelope: getEnvelopeLeaseDashboard('3502'),
    clickstream_trigger: {
      trigger_location_nm: 'leases_dashboard',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'lease_overflow',
      trigger_source_nm: 'deactivate_lease',
    },
    semantic: {
      semantic_event_nm: 'deactivate_lease',
      topic_tag_txt: ['zrm_leases'],
    },
    ...getZrmLeaseInfo(lease),
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/3503
export const emitGetStartedLeaseEvent = ({ user, listing }: ListingUserEventArgs) =>
  trackUserListingEvent({
    envelope: getEnvelopeZillowLease('3503'),
    clickstream_trigger: {
      trigger_location_nm: 'zillow_lease_wizard',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'parties_to_the_lease',
      trigger_source_nm: 'button_to_get_started',
    },
    semantic: {
      semantic_event_nm: 'continue_wizard_lease',
      topic_tag_txt: ['zrm_leases'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/3504
export const emitSaveAndContinueEvent = ({ user, listing }: ListingUserEventArgs) =>
  trackUserListingEvent({
    envelope: getEnvelopeZillowLease('3504'),
    clickstream_trigger: {
      trigger_location_nm: 'zillow_lease_wizard',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'parties_to_the_lease_landlord_info',
      trigger_source_nm: 'button_to_save_and_continue',
    },
    semantic: {
      semantic_event_nm: 'continue_wizard_lease',
      topic_tag_txt: ['zrm_leases'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/3506
export const emitViewSummaryEvent = ({ user, listing }: ListingUserEventArgs) =>
  trackUserListingEvent({
    envelope: getEnvelopeZillowLease('3506'),
    clickstream_trigger: {
      trigger_location_nm: 'zillow_lease_wizard',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'additional_details_upload_additional_doc',
      trigger_source_nm: 'view_summary',
    },
    semantic: {
      semantic_event_nm: 'view_summary',
      topic_tag_txt: ['zrm_leases'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/3508
export const emitPreviewFullLeaseBottomEvent = ({ user, listing }: ListingUserEventArgs) =>
  trackUserListingEvent({
    envelope: getEnvelopeZillowLease('3508'),
    clickstream_trigger: {
      trigger_location_nm: 'zillow_lease_wizard',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'bottom_summary',
      trigger_source_nm: 'button_to_preview_full_lease',
    },
    semantic: {
      semantic_event_nm: 'preview_lease',
      topic_tag_txt: ['zrm_leases'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/3507
export const emitPreviewFullLeaseTopEvent = ({ user, listing }: ListingUserEventArgs) =>
  trackUserListingEvent({
    envelope: getEnvelopeZillowLease('3507'),
    clickstream_trigger: {
      trigger_location_nm: 'zillow_lease_wizard',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'top_summary',
      trigger_source_nm: 'button_to_preview_full_lease',
    },
    semantic: {
      semantic_event_nm: 'preview_lease',
      topic_tag_txt: ['zrm_leases'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/3509
export const emitSaveForLaterEvent = ({ user, listing }: ListingUserEventArgs) =>
  trackUserListingEvent({
    envelope: getEnvelopeZillowLease('3509'),
    clickstream_trigger: {
      trigger_location_nm: 'zillow_lease_wizard',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'bottom_summary',
      trigger_source_nm: 'save_for_later',
    },
    semantic: {
      semantic_event_nm: 'save_for_later',
      topic_tag_txt: ['zrm_leases'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/3510
export const emitWizardViewClickEvent = ({ user, listing }: ListingUserEventArgs) =>
  trackUserListingEvent({
    envelope: getEnvelopeZillowLease('3510'),
    clickstream_trigger: {
      trigger_location_nm: 'zillow_lease_wizard',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'button_for_list_view',
    },
    semantic: {
      semantic_event_nm: 'use_list_view',
      topic_tag_txt: ['zrm_leases'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/3511
export const emitListViewClickEvent = ({ user, listing }: ListingUserEventArgs) =>
  trackUserListingEvent({
    envelope: getEnvelopeZillowLease('3511'),
    clickstream_trigger: {
      trigger_location_nm: 'zillow_lease_wizard',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'button_for_wizard_view',
    },
    semantic: {
      semantic_event_nm: 'use_wizard_view',
      topic_tag_txt: ['zrm_leases'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/3512
export const emitCreateLeaseContinueEvent = ({ user, listing }: ListingUserEventArgs) =>
  trackUserListingEvent({
    envelope: getEnvelopeZillowLease('3512'),
    clickstream_trigger: {
      trigger_location_nm: 'upload_lease_flow',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'self_upload_start',
      trigger_source_nm: 'button_to_continue',
    },
    semantic: {
      semantic_event_nm: 'continue_upload_lease',
      topic_tag_txt: ['zrm_leases'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/3521
export const emitEditZdocsEvent = ({ user, listing }: ListingUserEventArgs) =>
  trackUserListingEvent({
    envelope: getEnvelopeZillowLease('3521'),
    clickstream_trigger: {
      trigger_location_nm: 'zdocs',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'edit',
    },
    semantic: {
      semantic_event_nm: 'edit_lease',
      topic_tag_txt: ['zrm_leases'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/3522
export const emitYesEditLeaseEvent = ({ user, listing }: ListingUserEventArgs) =>
  trackUserListingEvent({
    envelope: getEnvelopeZillowLease('3522'),
    clickstream_trigger: {
      trigger_location_nm: 'zdocs',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'yes',
    },
    semantic: {
      semantic_event_nm: 'confirm_yes',
      topic_tag_txt: ['zrm_leases'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/3524
export const emitSaveDocumentEvent = ({ lease, user, listing }: LeaseListingUserEventArgs) =>
  trackLeaseListingUserEvent({
    envelope: getEnvelopeZillowLease('3524'),
    clickstream_trigger: {
      trigger_location_nm: 'zdocs',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'save_document',
    },
    semantic: {
      semantic_event_nm: 'save_document',
      topic_tag_txt: ['zrm_leases'],
    },
    ...getZrmLeaseInfo(lease),
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/3526
export const emitRequestSignatureZdocsEvent = ({ lease, user, listing }: LeaseListingUserEventArgs) =>
  trackLeaseListingUserEvent({
    envelope: getEnvelopeZillowLease('3526'),
    clickstream_trigger: {
      trigger_location_nm: 'zdocs',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'request_signature',
    },
    semantic: {
      semantic_event_nm: 'request_signature',
      topic_tag_txt: ['zrm_leases'],
    },
    ...getZrmLeaseInfo(lease),
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/3527
export const emitCancelSigningEvent = ({ lease, user, listing }: LeaseListingUserEventArgs) =>
  trackLeaseListingUserEvent({
    envelope: getEnvelopeZillowLease('3527'),
    clickstream_trigger: {
      trigger_location_nm: 'zdocs',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'cancel_signing',
    },
    semantic: {
      semantic_event_nm: 'cancel_signing',
      topic_tag_txt: ['zrm_leases'],
    },
    ...getZrmLeaseInfo(lease),
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/3530
export const emitRegisterToVoteEvent = ({ lease, user, listing }: LeaseListingUserEventArgs) =>
  trackLeaseListingUserEvent({
    envelope: getEnvelopeLeaseDashboard('3530'),
    clickstream_trigger: {
      trigger_location_nm: 'zdocs',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'register_to_vote',
    },
    semantic: {
      semantic_event_nm: 'register_to_vote',
      topic_tag_txt: ['zrm_leases'],
    },
    ...getZrmLeaseInfo(lease),
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/3531
export const emitGoToMyLeasesEvent = ({ lease, user, listing }: LeaseListingUserEventArgs) =>
  trackLeaseListingUserEvent({
    envelope: getEnvelopeZillowLease('3531'),
    clickstream_trigger: {
      trigger_location_nm: 'zdocs',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'go_to_my_leases',
    },
    semantic: {
      semantic_event_nm: 'go_to_dashboard',
      topic_tag_txt: ['zrm_leases'],
    },
    ...getZrmLeaseInfo(lease),
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/3505
export const emitSectionEightSaveAndContinueEvent = ({ user, listing }: ListingUserEventArgs) =>
  trackUserListingEvent({
    envelope: getEnvelopeLeaseDashboard('3505'),
    clickstream_trigger: {
      trigger_location_nm: 'zillow_lease_wizard',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'additional_details_section_8',
      trigger_source_nm: 'button_to_save_and_continue',
    },
    semantic: {
      semantic_event_nm: 'continue_wizard_lease',
      topic_tag_txt: ['zrm_leases'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/3513
export const emitLeaseTermsUploadLeaseSaveAndContinueEvent = ({ user, listing }: ListingUserEventArgs) =>
  trackUserListingEvent({
    envelope: getEnvelopeLeaseDashboard('3513'),
    clickstream_trigger: {
      trigger_location_nm: 'upload_lease_flow',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'lease_terms',
      trigger_source_nm: 'button_to_save_and_continue',
    },
    semantic: {
      semantic_event_nm: 'continue_upload_lease',
      topic_tag_txt: ['zrm_leases'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/3514
export const emitTenantInformationUploadLeaseSaveAndContinueEvent = ({ user, listing }: ListingUserEventArgs) =>
  trackUserListingEvent({
    envelope: getEnvelopeLeaseDashboard('3514'),
    clickstream_trigger: {
      trigger_location_nm: 'upload_lease_flow',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'tenant_info',
      trigger_source_nm: 'button_to_save_and_continue',
    },
    semantic: {
      semantic_event_nm: 'continue_upload_lease',
      topic_tag_txt: ['zrm_leases'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/3515
export const emitLandlordInformationUploadLeaseSaveAndContinueEvent = ({ user, listing }: ListingUserEventArgs) =>
  trackUserListingEvent({
    envelope: getEnvelopeLeaseDashboard('3515'),
    clickstream_trigger: {
      trigger_location_nm: 'upload_lease_flow',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'tenant_info',
      trigger_source_nm: 'button_to_save_and_continue',
    },
    semantic: {
      semantic_event_nm: 'continue_upload_lease',
      topic_tag_txt: ['zrm_leases'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/3516
export const emitRentAmountUploadLeaseSaveAndContinueEvent = ({ user, listing }: ListingUserEventArgs) =>
  trackUserListingEvent({
    envelope: getEnvelopeLeaseDashboard('3516'),
    clickstream_trigger: {
      trigger_location_nm: 'upload_lease_flow',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'rent_amount',
      trigger_source_nm: 'button_to_save_and_continue',
    },
    semantic: {
      semantic_event_nm: 'continue_upload_lease',
      topic_tag_txt: ['zrm_leases'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/3517
export const emitSecurityDepositUploadLeaseSaveAndContinueEvent = ({ user, listing }: ListingUserEventArgs) =>
  trackUserListingEvent({
    envelope: getEnvelopeLeaseDashboard('3517'),
    clickstream_trigger: {
      trigger_location_nm: 'upload_lease_flow',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'securitydeposit',
      trigger_source_nm: 'button_to_save_and_close',
    },
    semantic: {
      semantic_event_nm: 'continue_upload_lease',
      topic_tag_txt: ['zrm_leases'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/3518
export const emitNextTopEvent = ({ lease, user, listing }: LeaseListingUserEventArgs) =>
  trackLeaseListingUserEvent({
    envelope: getEnvelopeLeaseDashboard('3518'),
    clickstream_trigger: {
      trigger_location_nm: 'upload_lease_flow',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'top_summary',
      trigger_source_nm: 'button_for_next',
    },
    semantic: {
      semantic_event_nm: 'continue_to_zdocs',
      topic_tag_txt: ['zrm_leases'],
    },
    ...getZrmLeaseInfo(lease),
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/3519
export const emitNextBottomEvent = ({ lease, user, listing }: LeaseListingUserEventArgs) =>
  trackLeaseListingUserEvent({
    envelope: getEnvelopeLeaseDashboard('3519'),
    clickstream_trigger: {
      trigger_location_nm: 'upload_lease_flow',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'top_summary',
      trigger_source_nm: 'button_for_next',
    },
    semantic: {
      semantic_event_nm: 'continue_to_zdocs',
      topic_tag_txt: ['zrm_leases'],
    },
    ...getZrmLeaseInfo(lease),
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/3532
export const emitDownloadSignedPdfEvent = ({ lease, user, listing }: LeaseListingUserEventArgs) =>
  trackLeaseListingUserEvent({
    envelope: getEnvelopeLeaseDashboard('3532'),
    clickstream_trigger: {
      trigger_location_nm: 'renter_dashboard',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'renter_dashboard',
      trigger_source_nm: 'download_signed_pdf',
    },
    semantic: {
      semantic_event_nm: 'view_signed_lease',
      topic_tag_txt: ['renter_leases'],
    },
    ...getZrmLeaseInfo(lease),
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/3533
export const emitViewAndSignLeaseEvent = ({ lease, user, listing }: LeaseListingUserEventArgs) =>
  trackLeaseListingUserEvent({
    envelope: getEnvelopeLeaseDashboard('3533'),
    clickstream_trigger: {
      trigger_location_nm: 'renter_dashboard',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'view_and_sign_lease',
    },
    semantic: {
      semantic_event_nm: 'view_lease_to_sign',
      topic_tag_txt: ['renter_leases'],
    },
    ...getZrmLeaseInfo(lease),
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/3534
export const emitClickEmailViewLeaseEvent = ({ lease, user, listing }: LeaseListingUserEventArgs) =>
  trackLeaseListingUserEvent({
    envelope: getEnvelopeLeaseDashboard('3534'),
    clickstream_trigger: {
      trigger_location_nm: 'renter_dashboard',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'email',
      trigger_source_nm: 'view_lease',
    },
    semantic: {
      semantic_event_nm: 'click_email_view_lease',
      topic_tag_txt: ['renter_leases'],
    },
    ...getZrmLeaseInfo(lease),
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/4026
export const emitCancelActiveOpenEvent = ({ lease, user, listing }: LeaseListingUserEventArgs) =>
  trackLeaseListingUserEvent({
    envelope: getEnvelopeZillowLease('4026'),
    clickstream_trigger: {
      trigger_location_nm: 'leases_dashboard',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'leases_overflow_menu',
      trigger_source_nm: 'cancel_lease',
    },
    semantic: {
      semantic_event_nm: 'begin_lease_cancellation',
      topic_tag_txt: ['zrm_leases'],
    },
    ...getZrmLeaseInfo(lease),
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/4027
export const emitCancelActiveConfirmEvent = ({ lease, user, listing }: LeaseListingUserEventArgs) =>
  trackLeaseListingUserEvent({
    envelope: getEnvelopeZillowLease('4027'),
    clickstream_trigger: {
      trigger_location_nm: 'leases_dashboard',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'lease_cancellation_modal',
      trigger_source_nm: 'yes_cancel',
    },
    semantic: {
      semantic_event_nm: 'confirm_cancellation',
      topic_tag_txt: ['zrm_leases'],
    },
    ...getZrmLeaseInfo(lease),
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/4783
export const emitViewCreateLeaseEvent = ({ user, listing }: ListingUserEventArgs) =>
  trackUserListingEvent({
    envelope: getEnvelopeLease('4783'),
    clickstream_trigger: {
      trigger_location_nm: 'lease_selection_screen',
      trigger_type_nm: 'view',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'lease_selection_screen',
    },
    semantic: {
      semantic_event_nm: 'start_lease',
      topic_tag_txt: ['zrm_leases'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/7528
export const emitViewSampleLeaseEvent = ({ user, listing }: ListingUserEventArgs) =>
  trackUserListingEvent({
    envelope: getEnvelopeLease('7528'),
    clickstream_trigger: {
      trigger_location_nm: 'lease_selection_screen',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'sample_lease',
    },
    semantic: {
      semantic_event_nm: 'see_sample_lease',
      topic_tag_txt: ['zrm_leases'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/8138
export const emitEditAndShareGeneratedLeaseEvent = ({ user, listing }: ListingUserEventArgs) =>
  trackUserListingEvent({
    envelope: getEnvelopeLease('8138'),
    clickstream_trigger: {
      trigger_location_nm: 'lease_preview_screen',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'edit_and_share',
    },
    semantic: {
      semantic_event_nm: 'finish_creating_lease',
      topic_tag_txt: ['zrm_leases'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/8139
export const emitCloseGeneratedLeaseEvent = ({ user, listing }: ListingUserEventArgs) =>
  trackUserListingEvent({
    envelope: getEnvelopeLease('8139'),
    clickstream_trigger: {
      trigger_location_nm: 'lease_preview_screen',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'close_lease',
    },
    semantic: {
      semantic_event_nm: 'click_no_thanks',
      topic_tag_txt: ['zrm_leases'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/8868
export const emitViewLeasePreviewEvent = ({ user, listing }: ListingUserEventArgs) =>
  trackUserListingEvent({
    envelope: getEnvelopeLease('8868'),
    clickstream_trigger: {
      trigger_location_nm: 'lease_preview_screen',
      trigger_type_nm: 'view',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'lease_preview_screen',
    },
    semantic: {
      semantic_event_nm: 'view_content',
      topic_tag_txt: ['rental-leases'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/8899
export const emitClickCosmosTimelineCircleEvent = ({
  lease,
  user,
  listing,
  sectionName,
}: LeaseListingUserEventArgs & { sectionName: string }) =>
  trackLeaseListingUserEvent({
    envelope: getEnvelopeLease('8899'),
    clickstream_trigger: {
      trigger_location_nm: 'zillow_lease_wizard',
      trigger_type_nm: 'interaction',
      trigger_object_nm: sectionName,
      trigger_source_nm: 'lease_timeline_dot',
    },
    semantic: {
      semantic_event_nm: 'lease_builder_dot',
      topic_tag_txt: [],
    },
    ...getZrmLeaseInfo(lease),
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/8900
export const emitClickTimelineDotEvent = ({
  lease,
  user,
  listing,
  category,
}: LeaseListingUserEventArgs & { category: string }) =>
  trackLeaseListingUserEvent({
    envelope: getEnvelopeLease('8900'),
    clickstream_trigger: {
      trigger_location_nm: 'zillow_lease_wizard',
      trigger_type_nm: 'interaction',
      trigger_object_nm: category,
      trigger_source_nm: 'lease_timeline_dot',
    },
    semantic: {
      semantic_event_nm: 'lease_builder_dot',
      topic_tag_txt: [],
    },
    ...getZrmLeaseInfo(lease),
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/8901
export const emitClickNextLeaseBuilderEvent = ({
  lease,
  user,
  listing,
  stepName,
}: LeaseListingUserEventArgs & { stepName: string }) =>
  trackLeaseListingUserEvent({
    envelope: getEnvelopeLease('8901'),
    clickstream_trigger: {
      trigger_location_nm: 'zillow_lease_wizard',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'button_next_page',
    },
    semantic: {
      semantic_event_nm: 'next_lease_builder',
      topic_tag_txt: [],
    },
    ...getZrmLeaseInfo(lease, stepName),
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/8140
export const emitFeedbackModalSubmitEvent = ({ user, listing }: ListingUserEventArgs) =>
  trackUserListingEvent({
    envelope: getEnvelopeLease('8140'),
    clickstream_trigger: {
      trigger_location_nm: 'lease_preview_screen',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'no_thanks',
    },
    semantic: {
      semantic_event_nm: 'modal_no_thanks_feedback',
      topic_tag_txt: ['zrm_leases'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });
